import React from 'react';

import MainLayout from '@/layout/MainLayout';
import Home from '@/pages/Home';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import { User } from '@/redux/auth/authTypes';

const notFound = React.lazy(() => import('@/pages/NotFound'));
const listItems = React.lazy(() => import('@/pages/Items/ListItems'));
const AddItem = React.lazy(() => import('@/pages/Items/AddItem'));
const ImportItems = React.lazy(() => import('@/pages/Items/ImportItems'));
const listCategories = React.lazy(() => import('@/pages/categories/ListCategories'));
const addCategory = React.lazy(() => import('@/pages/categories/AddCategory'));
const listDiscounts = React.lazy(() => import('@/pages/discounts/ListDiscounts'));
const addDiscount = React.lazy(() => import('@/pages/discounts/AddDiscount'));
const addModifier = React.lazy(() => import('@/pages/modifiers/AddModifier'));
const listModifiers = React.lazy(() => import('@/pages/modifiers/ListModifiers'));
const ForgetPassword = React.lazy(() => import('@/pages/auth/ForgetPassword'));
const ResetPassword = React.lazy(() => import('@/pages/auth/ResetPassword'));
const confirmEmail = React.lazy(() => import('@/pages/auth/EmailConfirmation'));
const listEmployees = React.lazy(() => import('@/pages/employees/ListEmployees'));
const addEmployee = React.lazy(() => import('@/pages/employees/AddEmployee'));
const listRoles = React.lazy(() => import('@/pages/roles/ListRoles'));
const addRole = React.lazy(() => import('@/pages/roles/AddRole'));
const listTaxes = React.lazy(() => import('@/pages/settings/taxes/ListTaxes'));
const addTax = React.lazy(() => import('@/pages/settings/taxes/AddTax'));
const listFeatures = React.lazy(() => import('@/pages/settings/features/ListFeatures'));
const profile = React.lazy(() => import('@/pages/profile/Profile'));
const receiptSettings = React.lazy(() => import('@/pages/settings/receipt/ReceiptSettings'));
const listCustomers = React.lazy(() => import('@/pages/customers/ListCustomers'));
const addCustomer = React.lazy(() => import('@/pages/customers/AddCustomer'));
const listStores = React.lazy(() => import('@/pages/stores/ListStores'));
const addStore = React.lazy(() => import('@/pages/stores/AddStore'));
const listTables = React.lazy(() => import('@/pages/settings/tables/ListTables'));
const addTable = React.lazy(() => import('@/pages/settings/tables/AddTable'));
const listTableCategories = React.lazy(
  () => import('@/pages/settings/table-categories/ListTableCategories')
);
const addTableCategory = React.lazy(
  () => import('@/pages/settings/table-categories/AddTableCategory')
);
const ListPOSDevices = React.lazy(() => import('@/pages/pos devices/ListPOSDevices'));
const SalesByItem = React.lazy(() => import('@/pages/reports/SalesPerItem'));
const SalesPerEmployee = React.lazy(() => import('@/pages/reports/SalesPerEmployee'));
const SalesPerPayment = React.lazy(() => import('@/pages/reports/SalesPerPayment'));
const SalesPerCategory = React.lazy(() => import('@/pages/reports/SalesPerCategory'));
const SalesPerStore = React.lazy(() => import('@/pages/reports/SalesPerStore'));
const SalesPerPOS = React.lazy(() => import('@/pages/reports/SalesPerPOS'));
const ListInvoices = React.lazy(() => import('@/pages/reports/ListInvoices'));
const ListSuppliers = React.lazy(() => import('@/pages/inventory/ListSuppliers'));
const AddSupplier = React.lazy(() => import('@/pages/inventory/AddSupplier'));
const AddPurchaseOrder = React.lazy(
  () => import('@/pages/inventory/purchase-orders/AddPurchaseOrder')
);
const ListPurchaseOrders = React.lazy(
  () => import('@/pages/inventory/purchase-orders/ListPurchaseOrders')
);
const ViewPurchaseOrder = React.lazy(
  () => import('@/pages/inventory/purchase-orders/ViewPurchaseOrder')
);
const ReceivePurchaseOrder = React.lazy(
  () => import('@/pages/inventory/purchase-orders/ReceivePurchaseOrder')
);
const ListTransferOrders = React.lazy(
  () => import('@/pages/inventory/transfer-orders/ListTransferOrders')
);
const AddTransferOrder = React.lazy(
  () => import('@/pages/inventory/transfer-orders/AddTransferOrder')
);
const ViewTransferOrder = React.lazy(
  () => import('@/pages/inventory/transfer-orders/ViewTransferOrder')
);
const ListInventoryCounts = React.lazy(
  () => import('@/pages/inventory/inventory-count/ListInventoryCounts')
);
const CountInventoryCount = React.lazy(
  () => import('@/pages/inventory/inventory-count/CountInventoryCount')
);
const ViewInventoryCount = React.lazy(
  () => import('@/pages/inventory/inventory-count/ViewInventoryCount')
);
const AddInventoryCount = React.lazy(
  () => import('@/pages/inventory/inventory-count/AddInventoryCount')
);
const AdminDashboard = React.lazy(() => import('@/admin-dashboard/AdminDashboard'));
const UsersManagement = React.lazy(() => import('@/admin-dashboard/pages/UsersManagement'));
const ContentManagement = React.lazy(() => import('@/admin-dashboard/pages/ContentManagement'));
const SubscriptionsManagement = React.lazy(
  () => import('@/admin-dashboard/pages/SubscriptionsManagement')
);
const SystemSettings = React.lazy(() => import('@/admin-dashboard/pages/SystemSettings'));
const SecuritySettings = React.lazy(() => import('@/admin-dashboard/pages/SecuritySettings'));
const Analytics = React.lazy(() => import('@/admin-dashboard/pages/Analytics'));
const UpgradePage = React.lazy(() => import('@/pages/settings/UpgradePage'));
const InventoryModulePurchasePage = React.lazy(
  () => import('@/pages/settings/modules/InventoryModulePurchasePage')
);
const POSAnalytics = React.lazy(() => import('@/admin-dashboard/pages/POSAnalytics'));
const PrivacyPolicy = React.lazy(() => import('@/pages/PrivacyPolicy'));
const BillingPage = React.lazy(() => import('@/pages/settings/BillingPage'));
const PricingPage = React.lazy(() => import('@/pages/PricingPage'));

const routes = [
  {
    path: '/login',
    Component: Login,
  },
  {
    path: '/pricing',
    Component: PricingPage,
  },
  {
    path: '/register',
    Component: Register,
  },
  {
    path: '/forget-password',
    Component: ForgetPassword,
  },
  {
    path: '/reset-password',
    Component: ResetPassword,
  },
  {
    path: '/confirm-email',
    Component: confirmEmail,
  },
  {
    path: '/',
    Component: MainLayout,
    children: [
      {
        index: true,
        Component: Home,
      },
      {
        path: '/items/all',
        Component: listItems,
      },
      {
        path: '/items/add',
        Component: AddItem,
      },
      {
        path: '/items/import',
        Component: ImportItems,
      },
      {
        path: '/items/edit/:id',
        Component: AddItem,
      },
      {
        path: '/items/categories',
        Component: listCategories,
      },
      {
        path: '/items/categories/add',
        Component: addCategory,
      },
      {
        path: '/items/categories/edit/:id',
        Component: addCategory,
      },
      {
        path: '/items/discounts',
        Component: listDiscounts,
      },
      {
        path: '/items/discounts/add',
        Component: addDiscount,
      },
      {
        path: '/items/discounts/edit/:id',
        Component: addDiscount,
      },
      {
        path: '/items/modifiers',
        Component: listModifiers,
      },
      {
        path: '/items/modifiers/add',
        Component: addModifier,
      },
      {
        path: '/items/modifiers/edit/:id',
        Component: addModifier,
      },
      {
        path: '/employees/all',
        Component: listEmployees,
      },
      {
        path: '/employees/add',
        Component: addEmployee,
      },
      {
        path: '/employees/edit/:id',
        Component: addEmployee,
      },
      {
        path: '/employees/roles',
        Component: listRoles,
        permissionCB: (user: User) => user?.role?.name === 'owner',
      },
      {
        path: '/employees/roles/add',
        Component: addRole,
        permissionCB: (user: User) => user?.role?.name === 'owner',
      },
      {
        path: '/employees/roles/edit/:id',
        Component: addRole,
        permissionCB: (user: User) => user?.role?.name === 'owner',
      },
      {
        path: '/customers/all',
        Component: listCustomers,
      },
      {
        path: '/customers/add',
        Component: addCustomer,
      },
      {
        path: '/customers/edit/:id',
        Component: addCustomer,
      },
      {
        path: '/settings/features',
        Component: listFeatures,
        permissionCB: (user: User) => user?.role?.name === 'owner',
      },
      {
        path: '/settings/receipt',
        Component: receiptSettings,
        permissionCB: (user: User) => user?.role?.name === 'owner',
      },
      {
        path: '/settings/taxes',
        Component: listTaxes,
      },
      {
        path: '/settings/taxes/add',
        Component: addTax,
      },
      {
        path: '/settings/taxes/edit/:id',
        Component: addTax,
      },
      {
        path: '/settings/stores',
        Component: listStores,
      },
      {
        path: '/settings/stores/add',
        Component: addStore,
      },
      {
        path: '/settings/stores/edit/:id',
        Component: addStore,
      },
      {
        path: '/tables/all',
        Component: listTables,
      },
      {
        path: '/tables/add',
        Component: addTable,
      },
      {
        path: '/tables/edit/:id',
        Component: addTable,
      },
      {
        path: '/table-categories/all',
        Component: listTableCategories,
      },
      {
        path: '/table-categories/add',
        Component: addTableCategory,
      },
      {
        path: '/table-categories/edit/:id',
        Component: addTableCategory,
      },
      {
        path: '/settings/pos-devices',
        Component: ListPOSDevices,
      },
      {
        path: '/settings/upgrade',
        Component: UpgradePage,
      },
      {
        path: '/settings/upgrade/billing',
        Component: BillingPage,
      },
      {
        path: '/settings/modules/inventory',
        Component: InventoryModulePurchasePage,
      },
      {
        path: '/profile',
        Component: profile,
      },
      {
        path: '/reports/sales-per-item',
        Component: SalesByItem,
      },
      {
        path: '/reports/sales-per-employee',
        Component: SalesPerEmployee,
      },
      {
        path: '/reports/sales-per-payment',
        Component: SalesPerPayment,
      },
      {
        path: '/reports/sales-per-category',
        Component: SalesPerCategory,
      },
      {
        path: '/reports/sales-per-store',
        Component: SalesPerStore,
      },
      {
        path: '/reports/sales-per-pos',
        Component: SalesPerPOS,
      },
      {
        path: '/reports/invoices',
        Component: ListInvoices,
      },
      {
        path: '/inventory/suppliers',
        Component: ListSuppliers,
      },
      {
        path: '/inventory/suppliers/add',
        Component: AddSupplier,
      },
      {
        path: '/inventory/suppliers/edit/:id',
        Component: AddSupplier,
      },
      {
        path: '/inventory/purchase-orders',
        Component: ListPurchaseOrders,
      },
      {
        path: '/inventory/purchase-orders/add',
        Component: AddPurchaseOrder,
      },
      {
        path: '/inventory/purchase-orders/edit/:id',
        Component: AddPurchaseOrder,
      },
      {
        path: '/inventory/purchase-orders/view/:id',
        Component: ViewPurchaseOrder,
      },
      {
        path: '/inventory/purchase-orders/receive/:id',
        Component: ReceivePurchaseOrder,
      },
      {
        path: '/inventory/transfer-orders',
        Component: ListTransferOrders,
      },
      {
        path: '/inventory/transfer-orders/add',
        Component: AddTransferOrder,
      },
      {
        path: '/inventory/transfer-orders/edit/:id',
        Component: AddTransferOrder,
      },
      {
        path: '/inventory/transfer-orders/view/:id',
        Component: ViewTransferOrder,
      },
      {
        path: '/inventory/inventory-count',
        Component: ListInventoryCounts,
      },
      {
        path: '/inventory/inventory-count/add',
        Component: AddInventoryCount,
      },
      {
        path: '/inventory/inventory-count/edit/:id',
        Component: AddInventoryCount,
      },
      {
        path: '/inventory/inventory-count/view/:id',
        Component: ViewInventoryCount,
      },
      {
        path: '/inventory/inventory-count/count/:id',
        Component: CountInventoryCount,
      },
      {
        path: '/admin-dashboard',
        Component: AdminDashboard,
        children: [
          {
            index: true,
            Component: UsersManagement,
          },
          {
            path: 'users',
            Component: UsersManagement,
          },
          {
            path: 'content',
            Component: ContentManagement,
          },
          {
            path: 'subscriptions',
            Component: SubscriptionsManagement,
          },
          {
            path: 'system',
            Component: SystemSettings,
          },
          {
            path: 'security',
            Component: SecuritySettings,
          },
          {
            path: 'analytics',
            Component: Analytics,
          },
          {
            path: 'pos-analytics',
            Component: POSAnalytics,
          },
        ],
      },
      {
        path: '/404',
        Component: notFound,
      },
      {
        path: '*',
        Component: notFound,
      },
    ],
  },
  {
    path: '/privacy-policy',
    Component: PrivacyPolicy,
  },
];

export default routes;
